/**
 * The office's manual override of a pay assignment's amount — and the figure
 * the server would have paid, kept beside it.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
 *
 * API #537 moved the costing to the server. Both write doors on
 * /tickets/:id/pay-assignments now price a row from the worker's
 * `employee_pay_rates` row, the line's quantity and unit, and the line's tech
 * slots; the figures in the request body are not stored. That closed the hole
 * where a client number became the paid number (TNT's FT-04415 held four
 * APPROVED day_rate rows at $0.00 for a hand configured at $800).
 *
 * It also took away the office's only escape hatch for the cases the
 * calculation gets wrong — the minimum-day-rate absorption, the uneven crew day
 * split, and any line whose costing refuses outright. Migration 0371 brings it
 * back as a RECORDED DECISION rather than as an untyped number in a body:
 *
 *   calculated_amount      WHAT PAYS. Unchanged in meaning; every existing
 *                          reader (payroll Generate, the approve auto-feed,
 *                          the CSV) goes on reading it.
 *   computed_amount        what the server calculated. NULL is legitimate and
 *                          means the costing REFUSED — an override is allowed
 *                          to rescue a row that cannot be priced.
 *   amount_overridden      the deliberate flag.
 *   amount_overridden_by   who decided.
 *   amount_overridden_at   when.
 *
 * ── THE SHAPES THESE ARRIVE IN ─────────────────────────────────────────────
 *
 * GET /tickets/:id/pay-assignments selects `tpa.*`, so these four come back
 * SNAKE_CASE and untransformed — this endpoint is one of the raw-row reads,
 * not the camelCase norm in CLAUDE.md's serialization section. Verified
 * against the route, not assumed. And `NUMERIC(10,2)` arrives from the
 * Postgres driver as a STRING ("800.00"), which is why every amount here goes
 * through parseMoney rather than being compared or rendered directly: "800.00"
 * === 800 is false, and that is exactly the silent-wrong-field family.
 *
 * ── ABSENT IS NOT ZERO, AND HERE IT IS THE COMMON CASE ─────────────────────
 *
 * 0371 deliberately did NOT backfill. Measured against production read-only on
 * 2026-09-02: 273 live assignments, 0 overridden, 0 carrying a computed
 * figure. So "no computed figure on file" is not an edge case to defend
 * against — it is what every row on every existing ticket reads today, until
 * something re-saves it. Costing them now would measure months-old work
 * against today's rate grid, which is a guess wearing history's clothes.
 *
 * A row with no computed figure must therefore read as NOT COMPUTED and never
 * as $0.00, and the difference display must stay silent rather than claiming a
 * gap between a real amount and a zero nobody calculated.
 */
import { parseMoney } from './display-labels';
import { formatInstantDay } from './calendar-date';

/** The four override columns, as GET hands them over. */
export interface OverrideColumns {
  calculated_amount?: number | string | null;
  computed_amount?: number | string | null;
  amount_overridden?: boolean | null;
  amount_overridden_by?: string | null;
  amount_overridden_at?: string | null;
}

/**
 * What a row's amount is, and where it came from.
 *
 * FOUR STATES, and they are not collapsible into "overridden or not":
 *
 *   computed     nothing was overridden. The server's figure pays, and there
 *                is nothing to decorate — a row that agrees with itself gets
 *                no badge, no muted second number, no author line.
 *   agrees       overridden, but to the same figure the server computed. Still
 *                a recorded decision (somebody chose it) so the author shows,
 *                but there is no DIFFERENCE to show, and rendering "$800 vs
 *                $800" would be noise dressed as a finding.
 *   differs      overridden to something other than the computed figure. Both
 *                numbers show: paid primary, computed muted beside it.
 *   uncomputed   overridden on a row the costing REFUSED. There is no second
 *                figure, and the honest words are "not computed" — never 0,
 *                and never a blank that reads as agreement.
 */
export type OverrideState =
  | { kind: 'computed'; paid: number | null }
  | { kind: 'agrees'; paid: number; computed: number; by: string | null; at: string | null }
  | { kind: 'differs'; paid: number; computed: number; by: string | null; at: string | null }
  | { kind: 'uncomputed'; paid: number | null; by: string | null; at: string | null };

/**
 * Money either side of a comparison is `NUMERIC` off the wire, so both sides
 * are coerced before they meet. Half a cent is the same epsilon the API's own
 * difference report uses, so the two agree about what "the same figure" means.
 */
const CENT_EPSILON = 0.005;

export function overrideStateOf(a: OverrideColumns): OverrideState {
  const paid = parseMoney(a.calculated_amount);
  const computed = parseMoney(a.computed_amount);
  // `=== true`, not truthy: the column is a real boolean and an absent one is
  // not-overridden. A row read before 0371 landed carries undefined here.
  if (a.amount_overridden !== true) return { kind: 'computed', paid };

  const by = a.amount_overridden_by ?? null;
  const at = a.amount_overridden_at ?? null;
  if (computed === null || paid === null) return { kind: 'uncomputed', paid, by, at };
  return Math.abs(paid - computed) < CENT_EPSILON
    ? { kind: 'agrees', paid, computed, by, at }
    : { kind: 'differs', paid, computed, by, at };
}

/** Does this row carry a decision somebody made? */
export function isOverridden(a: OverrideColumns): boolean {
  return a.amount_overridden === true;
}

/**
 * How many rows on this ticket the office set by hand.
 *
 * The header count, and the reason migration 0371 carries a partial index on
 * (company_id, ticket_id) WHERE amount_overridden — this is the question a
 * reviewer opens the screen to ask.
 */
export function overriddenCount(rows: OverrideColumns[]): number {
  return rows.reduce((n, r) => (isOverridden(r) ? n + 1 : n), 0);
}

/**
 * The author of a decision, as a name.
 *
 * `amount_overridden_by` is a bare UUID: the GET joins `users` for the worker
 * the row PAYS, not for the person who overrode it. So the name is resolved
 * from the company user list the screen already holds.
 *
 * A UUID is never rendered. When the id names nobody in the list — a
 * deactivated colleague, a list that has not loaded — the honest answer is
 * that somebody set it and we cannot say who, which is what the empty return
 * signals to the caller. Echoing the raw id would be the label-map failure in
 * CLAUDE.md: a display gap reported as a data bug.
 */
export function overrideAuthorName(
  userId: string | null,
  users: { id: string; firstName?: string; lastName?: string; first_name?: string; last_name?: string }[],
): string | null {
  if (!userId) return null;
  const u = users.find(x => x.id === userId);
  if (!u) return null;
  const name = `${u.firstName ?? u.first_name ?? ''} ${u.lastName ?? u.last_name ?? ''}`.trim();
  return name || null;
}

/**
 * When the decision was made.
 *
 * Delegates to `formatInstantDay` — the shared instant formatter — rather than
 * reaching for a calendar-day helper or writing a third `toLocale*` call site.
 * The distinction it exists for is the one that matters here:
 * `amount_overridden_at` is a TIMESTAMPTZ, a real instant, NOT a calendar day
 * like `work_date` and its family. It must not go through the local-midnight
 * construction CLAUDE.md prescribes for those, and it must not have its UTC
 * prefix read off the string: an override made at 18:30 in Alberta is stored
 * as the next day in UTC, and reading the prefix would date the decision a day
 * late.
 *
 * The options are the argument this call adds. The month is SPELLED, because
 * 09/02 is two different days on two sides of an ocean and this string sits
 * next to money somebody may dispute; and the time of day is kept, because two
 * overrides on one row in one afternoon are told apart by nothing else.
 */
export function formatOverrideAt(at: string | null, timeZone: string | null): string | null {
  // In the TENANT's zone (companies.time_zone), never the reader's device.
  return formatInstantDay(at, timeZone, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
